package dairy.mynotes.addnotes

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dairy.mynotes.repository.AuthRepository
import dairy.mynotes.repository.Note
import dairy.mynotes.repository.NotesRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * UI State for Add Notes Screen
 */
data class AddNotesUiState(
    val description: String = "",
    val category: String = "",
    val availableCategories: List<String> = emptyList(),
    val isLoading: Boolean = false,
    val isSaved: Boolean = false,
    val error: String? = null
)

/**
 * ViewModel for Add Notes Screen
 * Handles note creation, category management, and validation
 */
class AddNotesViewModel : ViewModel() {
    private val notesRepository = NotesRepository()
    private val authRepository = AuthRepository()

    private val _uiState = MutableStateFlow(AddNotesUiState())
    val uiState: StateFlow<AddNotesUiState> = _uiState.asStateFlow()

    init {
        loadExistingCategories()
    }

    /**
     * Load existing categories from user's notes
     */
    private fun loadExistingCategories() {
        viewModelScope.launch {
            try {
                val userId = authRepository.getCurrentUserId()
                if (userId != null) {
                    notesRepository.getUserNotes(userId).onSuccess { notes ->
                        val categories = notes
                            .map { it.category }
                            .filter { it.isNotBlank() }
                            .distinct()
                            .sorted()
                        _uiState.value = _uiState.value.copy(availableCategories = categories)
                    }.onFailure { _ ->
                        // silently ignore — UI will show empty dropdown
                    }
                }
            } catch (_: Exception) {
                // silently ignore
            }
        }
    }

    /**
     * Update description
     */
    fun onDescriptionChange(description: String) {
        _uiState.value = _uiState.value.copy(description = description)
    }

    /**
     * Update category (either from dropdown selection or manual input)
     */
    fun onCategoryChange(category: String) {
        _uiState.value = _uiState.value.copy(category = category)
    }

    /**
     * Validate note inputs
     */
    private fun validateNote(): String? {
        val state = _uiState.value

        return when {
            state.description.isBlank() -> "Description is required"
            state.category.isBlank() -> "Category is required"
            else -> null
        }
    }

    /**
     * Save note to Firestore
     */
    fun saveNote() {
        val validationError = validateNote()
        if (validationError != null) {
            _uiState.value = _uiState.value.copy(error = validationError)
            return
        }

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isLoading = true, error = null)

            val userId = authRepository.getCurrentUserId()
            if (userId == null) {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    error = "User not logged in"
                )
                return@launch
            }

            val state = _uiState.value

            val note = Note(
                title = "",  // Empty - not used in UI
                content = state.description.trim(),
                category = state.category.trim(),
                userId = userId
            )

            notesRepository.saveNote(note).onSuccess {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isSaved = true
                )
            }.onFailure { _ ->
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    error = "Failed to save note"
                )
            }
        }
    }

    /**
     * Clear error message
     */
    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    /**
     * Reset form
     */
    fun resetForm() {
        _uiState.value = AddNotesUiState(availableCategories = _uiState.value.availableCategories)
    }
}
